import {createContext, useContext, useSyncExternalStore} from "react";
import {z as zod} from "zod";
import {jsonZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

// TODO: Is `IntellsysDevtoolsConfiguration` really the best name we can come up with?

export type IntellsysDevtoolsConfiguration = {
    fillDummyData: boolean;
    mockApiCalls: "succeed" | "fail" | null;
    showDebuggingPanels: boolean;
    insaneLogs: {
        theming: boolean;
        tanstackRouterDebugging: boolean;
        errorComponentLogs: boolean;
        notFoundComponentLogs: boolean;
    };
};

const intellsysDevtoolsConfigurationZodValidator = zod.object({
    fillDummyData: zod.boolean(),
    mockApiCalls: zod.union([zod.literal("succeed"), zod.literal("fail"), zod.null()]),
    showDebuggingPanels: zod.boolean(),
    insaneLogs: zod.object({
        theming: zod.boolean(),
        tanstackRouterDebugging: zod.boolean(),
        errorComponentLogs: zod.boolean(),
        notFoundComponentLogs: zod.boolean(),
    }),
});

const defaultIntellsysDevtoolsConfiguration: IntellsysDevtoolsConfiguration = {
    fillDummyData: import.meta.env.ENVIRONMENT === "prod" ? false : true,
    mockApiCalls: null,
    showDebuggingPanels: import.meta.env.ENVIRONMENT === "prod" ? false : true,
    insaneLogs: {
        theming: false,
        tanstackRouterDebugging: false,
        errorComponentLogs: true,
        notFoundComponentLogs: true,
    },
};

type Listener = () => void;

export class IntellsysDevtoolsClient {
    private configuration: IntellsysDevtoolsConfiguration;
    private listeners: Set<Listener> = new Set();

    constructor() {
        // TODO: Create a good localStorage wrapper for things like this (for type safety)
        const intellsysDevtoolsConfigurationResult = jsonZodValidator
            .pipe(intellsysDevtoolsConfigurationZodValidator)
            .safeParse(localStorage.getItem("intellsysDevtools"));
        let intellsysDevtoolsConfiguration: IntellsysDevtoolsConfiguration;
        if (intellsysDevtoolsConfigurationResult.success === true) {
            intellsysDevtoolsConfiguration = intellsysDevtoolsConfigurationResult.data;
        } else {
            intellsysDevtoolsConfiguration = defaultIntellsysDevtoolsConfiguration;
        }

        const configuration = intellsysDevtoolsConfiguration;

        this.configuration = configuration;
    }

    // Get current configuration
    getConfiguration(): IntellsysDevtoolsConfiguration {
        return this.configuration;
    }

    // Update configuration and notify subscribers
    setConfiguration(updater: IntellsysDevtoolsConfiguration | ((prev: IntellsysDevtoolsConfiguration) => IntellsysDevtoolsConfiguration)): void {
        if (typeof updater === "function") {
            this.configuration = updater(this.configuration);
        } else {
            this.configuration = updater;
        }

        // TODO: Create a good localStorage wrapper for things like this (for type safety)
        // TODO: Use a constant for localStorage key
        localStorage.setItem("intellsysDevtools", JSON.stringify(this.configuration));

        // Notify all listeners
        this.notify();
    }

    // Subscribe to changes
    subscribe(listener: Listener): () => void {
        this.listeners.add(listener);

        // Return unsubscribe function
        return () => {
            this.listeners.delete(listener);
        };
    }

    // Notify all subscribers
    private notify(): void {
        for (const listener of this.listeners) {
            listener();
        }
    }
}

type IntellsysDevtoolsClientContextData = IntellsysDevtoolsClient;

export const IntellsysDevtoolsClientContext = createContext<IntellsysDevtoolsClientContextData | undefined>(undefined);

// Hook to get the client instance
export function useIntellsysDevtoolsClient(): IntellsysDevtoolsClient {
    const client = useContext(IntellsysDevtoolsClientContext);
    if (client === undefined) {
        throw new Error("IntellsysDevtoolsClientContext.Provider not found");
    }
    return client;
}

// Hook to subscribe to configuration changes
export function useIntellsysDevtoolsConfiguration(): IntellsysDevtoolsConfiguration {
    const client = useIntellsysDevtoolsClient();

    // useSyncExternalStore is the modern way to subscribe to external state
    const configuration = useSyncExternalStore(
        // Subscribe function
        (callback) => client.subscribe(callback),
        // Get snapshot function
        () => client.getConfiguration(),
        // Get server snapshot (for SSR, same as client for this case)
        () => client.getConfiguration(),
    );

    return configuration;
}
